import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useOpenTemplateDraft,
  useWebsiteTemplateCatalogV3,
  WebsiteTemplateServiceError,
  type StoreWebsiteEditorStateV3,
  type SupabaseClient,
  type WebsiteTemplateCatalogItemV3,
  type WebsiteTemplateCategory,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';
import { useWebsiteT } from '../hooks/use-website-t';
import { resolveCanvasOrigins, type CanvasOrigins } from '../lib/admin-origins';
import { stageErrorKey } from '../lib/issue-messages';
import { templateEditPath, type TemplateLibraryNotice } from '../lib/template-routes';
import { TemplateCard } from './template-card';
import { TemplateCategoryFilter } from './template-category-filter';
import { TemplateDemoDialog } from './template-demo-dialog';
import { UseTemplateDialog } from './use-template-dialog';
import { WebsiteRuntimeNotice } from './website-runtime-notice';

export interface TemplateLibraryScreenProps {
  readonly storeId: string;
  readonly storeSlug: string;
  readonly state: StoreWebsiteEditorStateV3;
  /** Why the library is the first screen: no template yet, or an old design that could not be converted. */
  readonly reason?: 'no-template' | 'unmappable-legacy' | 'change';
  /** Epic #228 P3: the edit route could not open its template. */
  readonly notice?: TemplateLibraryNotice | null;
  readonly client?: SupabaseClient;
  readonly origins?: CanvasOrigins | null;
  readonly newMutationId?: () => string;
}

/**
 * Screen 1: the template library. It is the first screen only for a
 * merchant who has nothing to open; everyone else reaches it with "Change
 * template". The list comes from the server page by page, filtered by
 * business category on the server.
 *
 * Epic #228 P3: every template keeps its own draft. "Use template" (first
 * use) asks first; "Edit" reopens the saved draft directly. Both open the
 * template's draft on the server and go to its edit route; nothing is
 * published.
 */
export function TemplateLibraryScreen({
  storeId,
  storeSlug,
  state,
  reason = 'change',
  notice = null,
  client = supabase,
  origins,
  newMutationId = () => crypto.randomUUID(),
}: TemplateLibraryScreenProps): React.JSX.Element {
  const { t, dir, lang } = useWebsiteT();
  const configuredOrigins =
    origins === undefined
      ? resolveCanvasOrigins({
          currentOrigin: window.location.origin,
          publicAdminUrl: env.VITE_PUBLIC_ADMIN_URL,
          publicStorefrontUrl: env.VITE_PUBLIC_STOREFRONT_URL,
        })
      : origins;
  const creditsHref = configuredOrigins
    ? `${configuredOrigins.storefrontOrigin}/template-credits?lang=${lang}`
    : null;
  const navigate = useNavigate();
  const [category, setCategory] = useState<WebsiteTemplateCategory | null>(null);
  const [previewing, setPreviewing] = useState<WebsiteTemplateCatalogItemV3 | null>(null);
  const [using, setUsing] = useState<WebsiteTemplateCatalogItemV3 | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [openErrorKey, setOpenErrorKey] = useState<string | null>(null);
  const openDraft = useOpenTemplateDraft(client, storeId);

  const catalog = useWebsiteTemplateCatalogV3(client, {
    storeId,
    category,
    supabaseUrl: env.VITE_SUPABASE_URL,
  });
  const items = useMemo(
    () => catalog.data?.pages.flatMap((page) => page.items) ?? [],
    [catalog.data],
  );

  /** "Edit": reopen the template's saved draft, then go to its edit route. */
  const edit = async (item: WebsiteTemplateCatalogItemV3): Promise<void> => {
    if (opening !== null) return;
    setOpening(item.templateId);
    setOpenErrorKey(null);
    try {
      await openDraft.mutateAsync({
        templateId: item.templateId,
        clientMutationId: newMutationId(),
      });
      navigate(templateEditPath(item.templateId));
    } catch (error) {
      setOpenErrorKey(stageErrorKey(error));
    } finally {
      setOpening(null);
    }
  };
  /** First use asks first; a template with a saved draft opens it directly. */
  const choose = (item: WebsiteTemplateCatalogItemV3): void => {
    if (item.draft === null) setUsing(item);
    else void edit(item);
  };
  const runtimeUnavailable =
    catalog.error instanceof WebsiteTemplateServiceError &&
    catalog.error.code === 'template_unavailable';

  return (
    <div
      dir={dir}
      data-testid="template-library"
      className="mx-auto max-w-screen-xl space-y-6 px-4 py-8 sm:px-6"
    >
      <header className="space-y-2">
        <h1 className="font-display text-foreground text-3xl font-semibold">
          {t('admin.websiteTemplates.gallery.title')}
        </h1>
        <p className="text-foreground text-lg">{t('admin.websiteTemplates.gallery.heading')}</p>
        <p className="text-muted-foreground text-sm">
          {t('admin.websiteTemplates.gallery.subheading')}
        </p>
        {creditsHref ? (
          <a
            href={creditsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground inline-block rounded text-sm underline focus-visible:outline focus-visible:outline-2"
            data-testid="template-library-photo-credits"
          >
            {t('admin.websiteTemplates.gallery.credits')}
          </a>
        ) : null}
      </header>

      {notice !== null && !runtimeUnavailable ? (
        <p
          role="status"
          data-testid={`${notice}-notice`}
          className="border-border bg-admin-surface-muted rounded-lg border px-4 py-3 text-sm"
        >
          {notice === 'not-started'
            ? t('admin.websiteTemplates.gallery.notStarted')
            : t('admin.websiteTemplates.gallery.designUnavailable')}
        </p>
      ) : null}

      {reason === 'unmappable-legacy' ? (
        <p
          role="status"
          data-testid="unmappable-notice"
          className="border-border bg-admin-surface-muted rounded-lg border px-4 py-3 text-sm"
        >
          {t('admin.websiteTemplates.editor.unmappable')}
        </p>
      ) : null}

      <TemplateCategoryFilter value={category} onChange={setCategory} />

      {catalog.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="library-loading">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-80" />
          ))}
        </div>
      ) : catalog.isError ? (
        <div role="alert" data-testid="library-error" className="space-y-3 text-sm">
          {runtimeUnavailable ? (
            <WebsiteRuntimeNotice storeId={storeId} />
          ) : (
            <>
              <p className="text-destructive">{t('admin.websiteTemplates.gallery.loadError')}</p>
              <Button type="button" variant="outline" onClick={() => void catalog.refetch()}>
                {t('admin.websiteTemplates.common.retry')}
              </Button>
            </>
          )}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="library-empty">
          {t('admin.websiteTemplates.gallery.empty')}
        </p>
      ) : (
        <>
          {openErrorKey === null ? null : (
            <p role="alert" data-testid="library-open-error" className="text-destructive text-sm">
              {t(openErrorKey)}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="library-grid">
            {items.map((item) => (
              <TemplateCard
                key={item.templateId}
                item={item}
                onPreview={setPreviewing}
                onUse={choose}
                onEdit={(target) => void edit(target)}
                opening={opening === item.templateId}
                disabled={opening !== null}
              />
            ))}
          </div>
        </>
      )}

      {catalog.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={catalog.isFetchingNextPage}
            onClick={() => void catalog.fetchNextPage()}
          >
            {t('admin.websiteTemplates.gallery.loadMore')}
          </Button>
        </div>
      ) : null}

      {previewing !== null ? (
        <TemplateDemoDialog
          client={client}
          storeId={storeId}
          storeSlug={storeSlug}
          item={previewing}
          origins={origins}
          onClose={() => setPreviewing(null)}
          onUse={() => choose(previewing)}
        />
      ) : null}
      {using !== null ? (
        <UseTemplateDialog
          client={client}
          storeId={storeId}
          item={using}
          editorState={state}
          newMutationId={newMutationId}
          onClose={() => setUsing(null)}
          onDone={(templateId) => {
            setUsing(null);
            navigate(templateEditPath(templateId));
          }}
        />
      ) : null}
    </div>
  );
}
